import React, { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar, Avatar, Box, Chip, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Stack, Toolbar, Typography, Badge,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CalendarTodayIcon from "@mui/icons-material/CalendarTodayOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import dayjs from "dayjs";
import { BRAND } from "../theme";
import ThemeColorPicker from "../components/ThemeColorPicker";
import { useAuth } from "../auth/AuthContext";
import { useThemeMode } from "../ThemeModeProvider";
import { DISTRIBUTOR_NAV } from "./nav";

const SIDEBAR = 240;

const DistributorLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { username, logout } = useAuth();
  useThemeMode(); // live recolour on theme change
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const name = username || "John Doe";

  const isActive = (path: string) =>
    path === "/dashboard" ? location.pathname === "/dashboard" : location.pathname.startsWith(path);

  const go = (path: string) => { navigate(path); setMobileOpen(false); };

  const navList = (
    <List sx={{ px: 1.5 }}>
      {DISTRIBUTOR_NAV.map((item) => {
        const active = isActive(item.path);
        return (
          <ListItemButton key={item.path} onClick={() => go(item.path)}
            sx={{ borderRadius: 1.5, mb: 0.5, color: active ? "#fff" : "#bcd3c7",
              bgcolor: active ? BRAND.green : "transparent",
              "&:hover": { bgcolor: active ? BRAND.green : "rgba(255,255,255,.06)" } }}>
            <ListItemIcon sx={{ minWidth: 38, color: "inherit" }}>{item.icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}>{item.label}</ListItemText>
          </ListItemButton>
        );
      })}
    </List>
  );

  const brand = (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2.5, py: 2.5 }}>
      <StorefrontIcon sx={{ color: "#fff" }} />
      <Typography variant="h6" sx={{ color: "#fff" }}>Wholesale Hub</Typography>
    </Stack>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: BRAND.surface }}>
      {/* Permanent sidebar (md+) */}
      <Box sx={{ width: SIDEBAR, flexShrink: 0, bgcolor: BRAND.greenDark, color: "#dfebe4",
        display: { xs: "none", md: "flex" }, flexDirection: "column", position: "sticky", top: 0, height: "100vh" }}>
        {brand}
        {navList}
      </Box>

      {/* Temporary drawer (mobile) */}
      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: SIDEBAR, bgcolor: BRAND.greenDark, color: "#dfebe4" } }}>
        {brand}
        {navList}
      </Drawer>

      {/* Main */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppBar position="sticky" elevation={0} color="inherit" sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
          <Toolbar sx={{ gap: 1 }}>
            <IconButton edge="start" onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: "inline-flex", md: "none" } }} aria-label="Open navigation">
              <MenuIcon />
            </IconButton>
            <Box sx={{ flex: 1 }} />
            <Chip icon={<CalendarTodayIcon fontSize="small" />} label={dayjs().format("ddd, MMM D, YYYY")}
              variant="outlined" sx={{ fontWeight: 600, display: { xs: "none", sm: "flex" } }} />
            <ThemeColorPicker />
            <IconButton><Badge color="primary" variant="dot"><NotificationsNoneIcon /></Badge></IconButton>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ gap: 1 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: BRAND.green }}>{name.slice(0, 2).toUpperCase()}</Avatar>
              <Box sx={{ textAlign: "left", display: { xs: "none", sm: "block" } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>{name}</Typography>
                <Typography variant="caption" color="text.secondary">Distributor Admin</Typography>
              </Box>
            </IconButton>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={logout}><LogoutIcon fontSize="small" style={{ marginRight: 8 }} />Sign out</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: { xs: 2, md: 3 } }}><Outlet /></Box>
      </Box>
    </Box>
  );
};

export default DistributorLayout;
